import * as React from 'react';
import './Profile.scss';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import UserForm from '../../components/Forms/UserForm';
import { UserEntity, ProfessionalEntity } from '../../store/entities/state';
import TitledContainer from '../../components/TitledContainer';
import ProfessionalForm from '../../components/Forms/ProfessionalForm';
import Typography from '../../components/Typography';
import { AppointmentData } from '../../models';
import { getTotalForProfessional } from '../../shared/appointmint/payments';
import { useDispatch, useSelector } from 'react-redux';
import { getExtrasSettings, getPaymentsSettings } from '../../store/settings/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatEuros } from '../../shared/string';
import { getAppointmentsForProfessional, isLoadingEntities } from '../../store/entities/selectors';
import { getMyAppointmentsAsync } from '../../ajax/requests';
import { loadEntities, openAppointment } from '../../store/entities/actions';
import { serviceName } from '../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../store/settings/hooks';
import { ListGroup, ListGroupItem, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { getDateString } from '../../shared/utils';
import styled from 'styled-components';
import { GoogleCalendarCard } from '../Settings/IntegrationsSettings/GoogleCalendarSettings';
import AppButton from '../../components/AppButton';

const SummaryGrid = styled.div<{ children?: React.ReactNode }>`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-M);
    margin-top: var(--spacer-S);
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const MonthCard = styled.div<{ accent: string; children?: React.ReactNode }>`
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-top: var(--border-thick) solid ${p => p.accent};
    border-radius: 4px;
    padding: var(--spacer-S);
`;

const CardTitle = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--spacer-S);
`;

const CardTitleText = styled.span<{ children?: React.ReactNode }>`
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
`;

const AppointmentsBadge = styled.button<{ children?: React.ReactNode; onClick?: () => void }>`
    background: none;
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 12px;
    padding: 2px var(--spacer-XS);
    font-size: 0.75rem;
    color: var(--appBlue);
    cursor: pointer;
    &:hover {
        background: var(--bg-hover-light);
        border-color: var(--appBlue);
    }
`;

const TotalAmount = styled.div<{ accent: string; children?: React.ReactNode }>`
    font-size: 2rem;
    font-weight: 700;
    color: ${p => p.accent};
    margin-bottom: var(--spacer-S);
    line-height: 1;
`;

const BreakdownList = styled.ul<{ children?: React.ReactNode }>`
    list-style: none;
    padding: 0;
    margin: 0;
    border-top: var(--border-thin) solid var(--appLigthGrey);
    padding-top: var(--spacer-XS);
`;

const BreakdownItem = styled.li<{ children?: React.ReactNode }>`
    font-size: 0.85rem;
    color: var(--text-muted);
    padding: 2px 0;
    &:last-child {
        font-weight: 600;
        color: var(--text-body);
        margin-top: var(--spacer-XS);
        padding-top: var(--spacer-XS);
        border-top: var(--border-thin) solid var(--appLigthGrey);
    }
`;

export interface ProfileStateProps {
    user: UserEntity,
    isClient: boolean,
    appointments?: AppointmentData[],
    professional?: ProfessionalEntity,
    userHasChanged: boolean,
    professionalHasChanged: boolean,
    isSubmittingUser: boolean,
    isSubmittingProfessional: boolean,
}

export interface ProfileDispatchProps {
    userChange: (newData: UserEntity) => void,
    professionalChange: (newData: ProfessionalEntity) => void,
    submitUserEdition: (newData: UserEntity) => void,
    submitProfessionalEdition: (newData: ProfessionalEntity) => void,
    sendConfirmationEmail: () => void,
}

export type ProfileProps = ProfileStateProps & ProfileDispatchProps;

const Profile: React.FC<ProfileProps> = (props) => {
    const {
        user,
        isClient,
        professional,
        isSubmittingUser,
        isSubmittingProfessional,
        userHasChanged,
        professionalHasChanged,
        submitUserEdition,
        submitProfessionalEdition,
        userChange,
        professionalChange,
        sendConfirmationEmail,
    } = props;

    const t = useTranslations()
    const dispatch = useDispatch()
    const generalSettings = useGeneralSettings()

    const appointments = useSelector(getAppointmentsForProfessional(professional?.id ?? ''))
    const isLoadingAppointments = useSelector(isLoadingEntities('appointments'))
    const _paymentProfiles = useSelector(getPaymentsSettings)
    const paymentProfiles = useMemo(() => Object.fromEntries(_paymentProfiles.map(p => [p.id, p])), [_paymentProfiles])
    const _extrasConfig = useSelector(getExtrasSettings)
    const extrasConfig = useMemo(() => Object.fromEntries(_extrasConfig.map(p => [p.id, p])), [_extrasConfig])

    useEffect(() => {
        if(!user.professionalId) return
        const from = new Date(); from.setMonth(from.getMonth() - 1); from.setDate(1); // Begin of last month
        const to = new Date(); to.setMonth(to.getMonth() + 1); to.setDate(0); // End of current month
        const fromS = getDateString(from)
        const toS = getDateString(to)
        dispatch(loadEntities('appointments', () => getMyAppointmentsAsync(fromS, toS), 'NewResult'))
    }, [])

    const thisMonthAppointments = useMemo(() => {
        const thisMonth = new Date().toISOString().substring(0, 7)
        return appointments.filter(a => a.date.substring(0, 7) === thisMonth)
    }, [appointments])

    const lastMonthAppointments = useMemo(() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1)
        const lastMonth = d.toISOString().substring(0, 7)
        return appointments.filter(a => a.date.substring(0, 7) === lastMonth)
    }, [appointments])

    const [paymentThisMonth, paymentsDescThisMonth] = useMemo<[number, string[]]>(() => {
        if(!thisMonthAppointments.length || !professional) return [0, []]
        return getTotalForProfessional(thisMonthAppointments, professional, paymentProfiles, extrasConfig)
    }, [thisMonthAppointments, professional, paymentProfiles, extrasConfig])

    const [paymentLastMonth, paymentsDescLastMonth] = useMemo<[number, string[]]>(() => {
        if(!lastMonthAppointments.length || !professional) return [0, []]
        return getTotalForProfessional(lastMonthAppointments, professional, paymentProfiles, extrasConfig)
    }, [lastMonthAppointments, professional, paymentProfiles, extrasConfig])

    const [appointmentsToShow, setAppointmentsToShow] = useState<AppointmentData[] | null>(null)
    const openAppointmentCallback = useCallback((a: AppointmentData) => () => dispatch(openAppointment(a.id)), [])

    return (
        <TitledContainer id='profile' title={t(TK.profile)}>
            {professional && (
                <>
                    <ProfessionalForm
                        data={professional}
                        hasChanged={professionalHasChanged}
                        isSubmitting={isSubmittingProfessional}
                        canEdit
                        type='edit'
                        onChange={professionalChange}
                        onSave={submitProfessionalEdition}/>
                    <hr/>
                </>
            )}
            <UserForm
                data={user}
                isClient={isClient}
                hasChanged={userHasChanged}
                isSubmitting={isSubmittingUser}
                canEdit
                hidePermissions={!!professional}
                hidePictureAndName={!!professional}
                type='edit'
                onChange={userChange}
                onSave={submitUserEdition}
                onSendConfirmationEmail={sendConfirmationEmail}/>

            {professional && (
                <>
                    <hr/>
                    <GoogleCalendarCard id='profile-google-calendar' />
                </>
            )}

            {
                professional &&
                    <>
                        <hr/>

                        <h3>{t(TK.summary)}</h3>

                        { isLoadingAppointments ? <Typography>{t(TK.loading)}...</Typography> : (
                            <SummaryGrid>
                                <MonthCard accent='var(--appGreen)'>
                                    <CardTitle>
                                        <CardTitleText>{t(TK.thisMonth)}</CardTitleText>
                                        <AppointmentsBadge onClick={() => setAppointmentsToShow(thisMonthAppointments)}>
                                            {thisMonthAppointments.length} {t(TK.appointments).toLowerCase()}
                                        </AppointmentsBadge>
                                    </CardTitle>
                                    <TotalAmount accent='var(--appGreen)'>{formatEuros(paymentThisMonth)}</TotalAmount>
                                    {paymentsDescThisMonth.length > 0 && (
                                        <BreakdownList>
                                            {paymentsDescThisMonth.map((d, i) => <BreakdownItem key={i}>{d}</BreakdownItem>)}
                                            <BreakdownItem>{t(TK.total)}: {formatEuros(paymentThisMonth)}</BreakdownItem>
                                        </BreakdownList>
                                    )}
                                </MonthCard>

                                <MonthCard accent='var(--appBlue)'>
                                    <CardTitle>
                                        <CardTitleText>{t(TK.lastMonth)}</CardTitleText>
                                        <AppointmentsBadge onClick={() => setAppointmentsToShow(lastMonthAppointments)}>
                                            {lastMonthAppointments.length} {t(TK.appointments).toLowerCase()}
                                        </AppointmentsBadge>
                                    </CardTitle>
                                    <TotalAmount accent='var(--appBlue)'>{formatEuros(paymentLastMonth)}</TotalAmount>
                                    {paymentsDescLastMonth.length > 0 && (
                                        <BreakdownList>
                                            {paymentsDescLastMonth.map((d, i) => <BreakdownItem key={i}>{d}</BreakdownItem>)}
                                            <BreakdownItem>{t(TK.total)}: {formatEuros(paymentLastMonth)}</BreakdownItem>
                                        </BreakdownList>
                                    )}
                                </MonthCard>
                            </SummaryGrid>
                        )}
                    </>
            }

            <Modal size='lg' isOpen={appointmentsToShow !== null} toggle={() => setAppointmentsToShow(null)}>
                <ModalHeader toggle={() => setAppointmentsToShow(null)}>{t(TK.appointments)}</ModalHeader>
                <ModalBody>
                    <ListGroup>
                        {(appointmentsToShow ?? []).map(a => (
                            <ListGroupItem key={a.id}>
                                <AppButton color='link' style={{padding: 0, verticalAlign: 'top'}} onClick={openAppointmentCallback(a)}>{a.date} {a.start}</AppButton>
                                &nbsp;- <b>{a.clientName}</b> ({serviceName(a.type, generalSettings)})
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </ModalBody>
            </Modal>
        </TitledContainer>
    );
}

export default Profile;
